import type { PatientDetail, PaymentMethod } from '#shared/types';
import { ApiError } from '#client/lib/api';

export type PaymentMode = 'payment' | 'refund';

export interface PaymentDraft {
  amount: number;
  method: PaymentMethod | '';
  note: string;
}

export interface PaymentFieldErrors {
  amount?: string | undefined;
  method?: string | undefined;
  note?: string | undefined;
}

/**
 * Prefilled with what is owed, or with everything paid for a refund — the
 * common case is settling or reversing the whole thing.
 */
export function initialPaymentDraft(
  mode: PaymentMode,
  patient: Pick<PatientDetail, 'paid' | 'balance'>,
): PaymentDraft {
  return { amount: mode === 'refund' ? patient.paid : patient.balance, method: '', note: '' };
}

/** A refund is posted as a negative amount against the same ledger. */
/** What `POST /patients/:id/payments` is sent; the server validates it. */
export type PaymentBody = ReturnType<typeof buildPaymentBody>;

export function buildPaymentBody(mode: PaymentMode, draft: PaymentDraft) {
  return {
    amount: mode === 'refund' ? -draft.amount : draft.amount,
    method: draft.method || undefined,
    note: draft.note.trim() || null,
  };
}

/**
 * The server's per-field messages, or null when the failure was not about a
 * field — in which case the request's own message is shown for the form.
 */
export function readPaymentFieldErrors(caught: unknown): PaymentFieldErrors | null {
  if (!(caught instanceof ApiError) || !caught.fields) return null;
  return {
    amount: caught.fieldError('amount'),
    method: caught.fieldError('method'),
    note: caught.fieldError('note'),
  };
}
